import type { Skill } from '@deskworks/shared-types';
import { Link } from '@tanstack/react-router';
import { BookOpen, Bot, FileText, Plus, Upload } from 'lucide-react';
import { Page, PageHeader } from '@/components/page';
import { Section } from '@/components/section';
import { ImportSkillDialog, SkillDialog, SkillSourceBadge } from '@/components/skill-dialogs';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useSkills, useWorkspaceId } from '@/lib/hooks';
import { t } from '@/lib/i18n';

export function SkillsPage() {
  const workspaceId = useWorkspaceId();
  const skills = useSkills(workspaceId);
  const own = skills.data?.filter((s) => s.source === 'workspace') ?? [];
  const deskworks = skills.data?.filter((s) => s.source === 'deskworks') ?? [];

  return (
    <Page>
      <PageHeader
        title={t('Skills')}
        description={t('Know-how for agents, in the Agent Skills format used by Claude and ChatGPT. Agents see each skill’s description and load the instructions only when a task needs them. You choose the skills per agent.')}
        actions={
          <>
            <ImportSkillDialog workspaceId={workspaceId}>
              <Button variant="outline">
                <Upload /> {t('Import')}
              </Button>
            </ImportSkillDialog>
            <SkillDialog workspaceId={workspaceId}>
              <Button>
                <Plus /> {t('New skill')}
              </Button>
            </SkillDialog>
          </>
        }
      />
      {skills.isPending ? (
        <LoadingRows rows={4} />
      ) : skills.isError ? (
        <ErrorState error={skills.error} onRetry={() => void skills.refetch()} />
      ) : (
        <div className="flex flex-col gap-6">
          <Section icon={<BookOpen />} title={t('Your skills')} description={t('Maintained in this workspace. A skill named like a Deskworks skill replaces it here.')}>
            {own.length === 0 ? (
              <EmptyState
                compact
                title={t('No skills of your own yet')}
                description={t('Create one, import a skill from Claude or ChatGPT, or customize a Deskworks skill below.')}
              />
            ) : (
              <SkillList skills={own} workspaceId={workspaceId} />
            )}
          </Section>
          <Section icon={<BookOpen />} title={t('Deskworks skills')} description={t('Shipped and updated with Deskworks. Customize one to get your own editable copy.')}>
            {deskworks.length === 0 ? <EmptyState compact title={t('No Deskworks skills installed')} /> : <SkillList skills={deskworks} workspaceId={workspaceId} />}
          </Section>
        </div>
      )}
    </Page>
  );
}

function SkillList({ skills, workspaceId }: { skills: Skill[]; workspaceId: string }) {
  return (
    <ul className="-mx-5 -mb-5 divide-y border-t">
      {skills.map((s) => (
        <li key={s.id}>
          <Link
            to="/w/$workspaceId/skills/$skillId"
            params={{ workspaceId, skillId: s.id }}
            className="group flex items-start gap-4 px-5 py-3 hover:bg-muted/50"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-mono text-[13px] font-medium group-hover:text-primary">{s.name}</span>
                <SkillSourceBadge skill={s} />
                {s.overriddenBy && <Badge tone="muted">{t('replaced by your version')}</Badge>}
                {s.status === 'disabled' && <Badge tone="amber">{t('disabled')}</Badge>}
              </div>
              <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{s.description}</div>
            </div>
            <div className="hidden shrink-0 items-center gap-3 pt-0.5 text-xs text-muted-foreground sm:flex">
              <span className="inline-flex items-center gap-1" title={t('Extra files')}>
                <FileText className="size-3.5" />
                {s.fileCount}
              </span>
              <span className="inline-flex items-center gap-1" title={t('Agents using it')}>
                <Bot className="size-3.5" />
                {s.agentCount}
              </span>
              <span className="w-8 text-right">v{s.version}</span>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}
